import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getMarketingEmailTemplatesV2 } from 'services/MailingService';
import { stripDiacritics } from 'utils/strings';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Marketing email templates list: same GET as template picker (`getMarketingEmailTemplatesV2`),
 * without custom pagination headers (those can break in browser due to CORS preflight).
 * Search and paging are client-side (aligned with `TemplatesPopover` search behavior).
 *
 * @param {object} options
 * @param {string} [options.searchQuery]
 * @param {number} [options.pageSize]
 */
export function useMarketingTemplates({ searchQuery = '', pageSize = DEFAULT_PAGE_SIZE } = {}) {
    const [sourceTemplates, setSourceTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visibleCount, setVisibleCount] = useState(pageSize);

    const requestGenerationRef = useRef(0);

    const filtered = useMemo(() => {
        const raw = stripDiacritics((searchQuery || '').toLowerCase().trim());
        if (!raw) {
            return sourceTemplates;
        }
        return sourceTemplates.filter((t) =>
            stripDiacritics((t?.name || '').toLowerCase().trim()).includes(raw),
        );
    }, [sourceTemplates, searchQuery]);

    useEffect(() => {
        setVisibleCount(pageSize);
    }, [searchQuery, pageSize]);

    const templates = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

    const hasMore = visibleCount < filtered.length;

    const loadMore = useCallback(() => {
        setVisibleCount((c) => Math.min(c + pageSize, filtered.length));
    }, [filtered.length, pageSize]);

    const loadInitial = useCallback(async () => {
        const generation = ++requestGenerationRef.current;
        setError(null);
        setIsLoading(true);

        try {
            const data = await getMarketingEmailTemplatesV2();
            if (generation !== requestGenerationRef.current) {
                return;
            }
            setSourceTemplates(Array.isArray(data) ? data : []);
            setVisibleCount(pageSize);
            setError(null);
        } catch (e) {
            if (generation !== requestGenerationRef.current) {
                return;
            }
            setSourceTemplates([]);
            setError(e);
        } finally {
            if (generation === requestGenerationRef.current) {
                setIsLoading(false);
            }
        }
    }, [pageSize]);

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const refetch = useCallback(() => {
        loadInitial();
    }, [loadInitial]);

    return {
        templates,
        isLoading,
        error,
        hasMore,
        loadMore,
        refetch,
    };
}
